"use client";

import { useState } from "react";
import Image from "next/image";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";

type Props = {
  images: string[];
  title: string;
  gradient: string;
};

export function ProjectGallery({ images, title, gradient }: Props) {
  const [lightbox, setLightbox] = useState<number | null>(null);

  function prev() { setLightbox(l => (l !== null ? (l - 1 + images.length) % images.length : null)); }
  function next() { setLightbox(l => (l !== null ? (l + 1) % images.length : null)); }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
    if (e.key === "Escape") setLightbox(null);
  }

  const grid =
    images.length === 1 ? "grid-cols-1" :
    images.length === 2 ? "grid-cols-2" :
    images.length === 3 ? "grid-cols-3" :
    "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";

  return (
    <>
      <div className="space-y-3">
        <div className={`inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground`}>
          <Images className="w-4 h-4" />
          Galerija ({images.length} {images.length === 1 ? "slika" : "slika"})
        </div>
        <div className={`grid ${grid} gap-3`}>
          {images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(i)}
              className={`group relative overflow-hidden rounded-2xl bg-muted cursor-zoom-in ${
                images.length === 1 ? "aspect-[16/7]" : images.length <= 3 ? "aspect-[4/3]" : "aspect-square"
              } shadow-sm hover:shadow-xl transition-all duration-300`}
            >
              <Image
                src={img}
                alt={`${title} — slika ${i + 1}`}
                fill
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Number badge */}
              <div className={`absolute top-2 left-2 w-6 h-6 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
                <span className="text-white text-xs font-bold">{i + 1}</span>
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
          onClick={() => setLightbox(null)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
          aria-label="Galerija slika"
        >
          {/* Close */}
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Zatvori"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 text-white text-sm font-medium">
            {lightbox + 1} / {images.length}
          </div>

          {/* Prev */}
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              aria-label="Prethodna"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Image */}
          <div
            className="relative max-w-5xl max-h-[80vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={images[lightbox]}
              alt={`${title} — slika ${lightbox + 1}`}
              fill
              sizes="90vw"
              className="object-contain"
            />
          </div>

          {/* Next */}
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              aria-label="Sljedeća"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Thumbnails strip */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 px-4 overflow-x-auto max-w-full">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setLightbox(i); }}
                  className={`w-14 h-10 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${i === lightbox ? "border-white scale-110" : "border-transparent opacity-50 hover:opacity-80"}`}
                >
                  <Image src={img} alt="" width={56} height={40} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
